import { Suspense, lazy, useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HeroScene = lazy(() => import("./HeroScene"));

const HEADLINE = ["We don't just", "market brands.", "We scale them."];

const STRAPLINE = [
  { lead: "Your", key: "Story", color: "#e7008a" },
  { lead: "Our", key: "Tech", color: "#7c34eb" },
  { lead: "Infinite", key: "Reach", color: "#f97e0d" },
];

function supportsWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.4 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const [webgl, setWebgl] = useState(false);
  const [reduced, setReduced] = useState(false);
  const sceneRef = useRef(null);
  const inView = useInView(sceneRef, { margin: "0px 0px -20% 0px" });

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    setWebgl(supportsWebGL());
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={sceneRef}
      className="relative h-screen w-full flex flex-col justify-center overflow-hidden"
    >
      {/* 3D scene or fallback — constrained to the right on desktop so the
          left-aligned headline stays fully readable */}
      {webgl && !reduced ? (
        <div className="absolute inset-y-0 right-0 w-full lg:w-[60%] opacity-60 lg:opacity-100">
          {inView && (
            <Suspense fallback={<div className="absolute inset-0 bg-[var(--bg-primary)]" />}>
              <HeroScene />
            </Suspense>
          )}
        </div>
      ) : (
        <div className="absolute inset-0">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[70vw] h-[70vw] max-w-[640px] max-h-[640px] rounded-full bg-[radial-gradient(circle,rgba(6,182,212,0.35),rgba(124,58,237,0.25)_40%,transparent_68%)] blur-3xl" />
        </div>
      )}

      {/* Directional scrim: darker on the left (text) fading to clear on the right (3D) */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[var(--bg-primary)] via-[var(--bg-primary)]/85 lg:via-[var(--bg-primary)]/55 to-transparent" />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[var(--bg-primary)]/50 via-transparent to-[var(--bg-primary)]" />

      <div className="relative z-10 px-6 md:px-12 lg:px-16 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 md:mb-8 text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[var(--accent-primary)]"
          data-testid="hero-eyebrow"
        >
          01 — Our 360° Marketing, Advertising & PR Services
        </motion.p>

        <h1
          className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-[13vw] sm:text-[11vw] md:text-[8.5vw] lg:text-[7.5vw]"
          data-testid="hero-headline"
        >
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
              >
                {i === 2 ? (
                  <span className="text-gradient-brand">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div
          className="mt-6 md:mt-8 flex flex-wrap items-center gap-x-2 gap-y-1 font-display text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight"
          data-testid="hero-strapline"
        >
          {STRAPLINE.map((seg, i) => (
            <span key={i} className="inline-flex overflow-hidden">
              <motion.span
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ delay: 1.25 + i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="inline-block"
              >
                <span className="text-white/80">{seg.lead} </span>
                <span
                  className="strapline-key"
                  style={{ color: seg.color, "--kc": seg.color }}
                >
                  {seg.key}
                </span>
                <span className="text-white/40">.</span>
              </motion.span>
            </span>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed">
            At Future Techmates, we deliver full-spectrum marketing, advertising, and
            PR solutions designed to help brands stand out, scale up, and stay
            relevant. From strategy to execution, every service connects creativity,
            technology, and measurable impact.
          </p>
          <a
            href="#contact"
            data-testid="hero-cta-button"
            data-magnetic
            className="shrink-0 inline-flex items-center gap-3 rounded-full bg-[var(--accent-secondary)] px-8 py-4 text-sm font-semibold text-white hover:bg-[var(--accent-secondary-hover)] transition-colors"
          >
            Free Consultation
            <span className="inline-block w-2 h-2 rounded-full bg-white" />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-zinc-500"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
